import type { CookLogApiItem } from "@recipevault/shared";

function mean(nums: number[]): number {
  return nums.reduce((s, n) => s + n, 0) / nums.length;
}
function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}
/** elapsed prefers a manual override when present. */
function elapsedOf(l: CookLogApiItem): number | null {
  return l.durationOverride ?? l.durationElapsedMin;
}
function cadencePhrase(daysPerCook: number): string {
  if (daysPerCook <= 10) return "every week or so";
  if (daysPerCook <= 45) return "about once a month";
  if (daysPerCook <= 120) return "every few months";
  return "once in a while";
}

/**
 * Up to 4 short, plain-text "what we noticed" lines for the just-finished cook.
 * `logs` is the recipe's full cook-log list (incl. this cook, written synchronously
 * by the complete handler). `steps` is the recipe's steps (ids, in order) for labels.
 */
export function deriveCookInsights(
  logs: CookLogApiItem[],
  thisLogId: string,
  steps: { id: string }[],
): string[] {
  // Dedupe by sessionId (keep earliest completedAt), drop soft-deleted, sort chronologically.
  const bySession = new Map<string, CookLogApiItem>();
  for (const log of logs) {
    if (log.deletedAt) continue;
    const existing = bySession.get(log.sessionId);
    if (!existing || log.completedAt < existing.completedAt) bySession.set(log.sessionId, log);
  }
  const deduped = [...bySession.values()].sort((a, b) => a.completedAt.localeCompare(b.completedAt));
  const thisLog = deduped.find((l) => l.id === thisLogId);
  if (!thisLog) return [];

  const n = deduped.length;
  const others = deduped.filter((l) => l.id !== thisLogId);
  const out: string[] = [];

  // Milestone.
  out.push(n === 1 ? "First time cooking this." : `${ordinal(n)} time cooking this.`);

  // This cook vs average active time (≥1 other with a non-null active value, delta ≥ 5 min).
  if (thisLog.durationActiveMin != null) {
    const otherActives = others.map((l) => l.durationActiveMin).filter((v): v is number => v != null);
    if (otherActives.length >= 1) {
      const delta = Math.round(mean(otherActives) - thisLog.durationActiveMin);
      if (delta >= 5) out.push(`${thisLog.durationActiveMin} min active — about ${delta} min faster than usual.`);
      else if (delta <= -5) out.push(`${thisLog.durationActiveMin} min active — about ${-delta} min longer than usual.`);
    }
  }

  // Fastest yet (≥2 cooks; this cook's elapsed is the minimum).
  const thisElapsed = elapsedOf(thisLog);
  if (n >= 2 && thisElapsed != null) {
    const all = deduped.map(elapsedOf).filter((v): v is number => v != null);
    if (all.length >= 2 && thisElapsed <= Math.min(...all)) out.push("Fastest cook yet.");
  }

  // Per-step: a step that ran notably long vs its average (else a parallel-overlap note).
  const labelFor = (id: string) => {
    const idx = steps.findIndex((s) => s.id === id);
    return idx >= 0 ? `Step ${idx + 1}` : "A step";
  };
  let long: { id: string; gap: number; cur: number; avg: number } | null = null;
  for (const [stepId, sd] of Object.entries(thisLog.byStepDuration)) {
    const runs = others.map((l) => l.byStepDuration[stepId]?.activeMin).filter((v): v is number => v != null);
    if (runs.length >= 1) {
      const avg = mean(runs);
      const gap = sd.activeMin - avg;
      if (sd.activeMin > avg * 1.25 && gap >= 3 && (!long || gap > long.gap)) {
        long = { id: stepId, gap, cur: sd.activeMin, avg };
      }
    }
  }
  if (long) {
    out.push(`${labelFor(long.id)} ran long — ${Math.round(long.cur)} min vs ~${Math.round(long.avg)} usual.`);
  } else if (Object.values(thisLog.byStepDuration).some((sd) => sd.hadParallelOverlap)) {
    out.push("You ran two steps at once — nice multitasking.");
  }

  // Cadence (≥3 cooks spanning ≥1 day).
  if (n >= 3) {
    const spanDays =
      (new Date(deduped[n - 1]!.completedAt).getTime() - new Date(deduped[0]!.completedAt).getTime()) / 86_400_000;
    if (spanDays >= 1) out.push(`You've cooked this ${n} times — ${cadencePhrase(spanDays / (n - 1))}.`);
  }

  return out.slice(0, 4);
}
